/**
 * dashboard.ts
 * Compras (REQUI) module — Tablero de Compras (GET /procurement/dashboard).
 * Cross-cutting (aggregates open orders/committed amount/funnel/top
 * suppliers/recent cases across every other section) — kept at the root of
 * contracts, not inside a section folder (openspec/arquitectura/convencion-
 * modulo.md §2), same posture as RRHH's dashboard.ts.
 *
 * refactor/compras-by-section — pure move out of index.ts (no shape/behavior change).
 */
import { z } from 'zod';

import { PRIORITIES } from './shared.js';

// ---------------------------------------------------------------------------
// Tablero de Compras (REQUI dashboard) — GET /procurement/dashboard. Shape
// mirrors LocxDashboardResponse/NomiDashboardResponse: many small typed
// sub-objects/arrays composed by the use case, not a single flat blob. No
// fabricated comparison baselines — every *ChangePercent field is
// number | null (percentChange convention, see compras'
// domain/dashboard-buckets.ts and LocxDashboardResponse's
// totalLitersChangePercent for precedent).
// ---------------------------------------------------------------------------

export const DASHBOARD_FUNNEL_STAGES = [
  'pendiente_aprobacion',
  'en_cotizacion',
  'adjudicacion',
  'en_ejecucion',
  'cerrada',
] as const;

export const DashboardFunnelBucketSchema = z.object({
  stage: z.enum(DASHBOARD_FUNNEL_STAGES),
  label: z.string(),
  count: z.number(),
});
export type DashboardFunnelBucket = z.infer<typeof DashboardFunnelBucketSchema>;

export const DashboardPoStatusBucketSchema = z.object({
  status: z.string(),
  label: z.string(),
  count: z.number(),
});
export type DashboardPoStatusBucket = z.infer<typeof DashboardPoStatusBucketSchema>;

export const DashboardTopSupplierSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  committedAmount: z.string(),
});
export type DashboardTopSupplier = z.infer<typeof DashboardTopSupplierSchema>;

/**
 * One purchase_case created inside the Tablero de Compras calendar's visible
 * month (compras-solicitudes-visibilidad-alertas, Slice F). RENAMED from
 * `DashboardUpcomingNeededBySchema`/`upcomingNeededBy` — the dashboard
 * calendar's axis moved from fecha-de-necesidad (forward-looking, excluding
 * cerrada/cancelada) to fecha-de-creación (historical, all statuses); keeping
 * the old name would have made the field lie about its own content.
 */
export const DashboardRecentCaseSchema = z.object({
  caseId: z.string().uuid(),
  code: z.string(),
  title: z.string(),
  createdAt: z.string(),
  priority: z.enum(PRIORITIES).nullable(),
});
export type DashboardRecentCase = z.infer<typeof DashboardRecentCaseSchema>;

/** One PO-level row in the paginated "open orders" table (filters live at the use-case input). */
export const DashboardOpenOrderRowSchema = z.object({
  poId: z.string().uuid(),
  code: z.string(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  status: z.string(),
  statusLabel: z.string(),
  total: z.string(),
  currency: z.string(),
  buyerId: z.string().uuid().nullable(),
  buyerName: z.string().nullable(),
  costCenterId: z.string().uuid().nullable(),
  costCenterName: z.string().nullable(),
  deliveryLocationId: z.string().uuid().nullable(),
  deliveryLocationName: z.string().nullable(),
  categoryLabel: z.string().nullable(),
  neededBy: z.string().nullable(),
  /**
   * requi-cotizacion-consolidada-por-rubro (Slice B, design.md D6) — count
   * of distinct SAs behind this OC. `1` for every single-SA OC (unchanged).
   * `> 1` marks a consolidated OC whose lines span multiple SAs — the
   * frontend renders "Varias" instead of buyerName/costCenterName/etc,
   * which are `null` in that case.
   */
  caseCount: z.number(),
});
export type DashboardOpenOrderRow = z.infer<typeof DashboardOpenOrderRowSchema>;

export const DashboardOpenOrdersPageSchema = z.object({
  items: z.array(DashboardOpenOrderRowSchema),
  totalCount: z.number(),
  totalPages: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type DashboardOpenOrdersPage = z.infer<typeof DashboardOpenOrdersPageSchema>;

export const ComprasDashboardResponseSchema = z.object({
  period: z.object({ from: z.string(), to: z.string() }),
  openOrdersCount: z.number(),
  openOrdersCountChangePercent: z.number().nullable(),
  committedAmount: z.string(),
  committedAmountChangePercent: z.number().nullable(),
  avgApprovalTimeHours: z.number().nullable(),
  avgApprovalTimeHoursChangePercent: z.number().nullable(),
  funnel: z.array(DashboardFunnelBucketSchema),
  cancelledCount: z.number(),
  poStatusBreakdown: z.array(DashboardPoStatusBucketSchema),
  topSuppliersByCommitted: z.array(DashboardTopSupplierSchema),
  recentCases: z.array(DashboardRecentCaseSchema),
  openOrders: DashboardOpenOrdersPageSchema,
});
export type ComprasDashboardResponse = z.infer<typeof ComprasDashboardResponseSchema>;

// ---------------------------------------------------------------------------
// GET /procurement/dashboard/spend-breakdown?dimension=costCenter|area&from&to — backlog #11: committed spend RANKED
// per cost center or per Área / Yacimiento (business unit). Summed from purchase-order LINES (a consolidated OC spreads
// over the cost center / area of each SA), same statuses and period as the dashboard's committedAmount. `id: null` =
// SAs without that dimension ("Sin centro de costo" / "Sin área").
// ---------------------------------------------------------------------------

export const SPEND_DIMENSIONS = ['costCenter', 'area'] as const;
export const SpendDimensionSchema = z.enum(SPEND_DIMENSIONS);
export type SpendDimension = z.infer<typeof SpendDimensionSchema>;

export const SpendBreakdownRowSchema = z.object({
  id: z.string().uuid().nullable(),
  name: z.string(),
  /** Σ line qty × unit price of the period's committed POs (no currency conversion, like committedAmount). */
  committedAmount: z.string(),
  previousCommittedAmount: z.string(),
  /** null without a previous-period baseline (percentChange convention). */
  changePercent: z.number().nullable(),
  /** Distinct POs with at least one line here. */
  purchaseOrderCount: z.number().int(),
  /** SAs still in progress without a purchase order (not en_ejecucion / cerrada / cancelada / borrador). */
  openCaseCount: z.number().int(),
  /** Share of the period's committed total, 0–100 (one decimal). */
  sharePercent: z.number(),
});
export type SpendBreakdownRow = z.infer<typeof SpendBreakdownRowSchema>;

export const SpendBreakdownResponseSchema = z.object({
  dimension: SpendDimensionSchema,
  period: z.object({ from: z.string(), to: z.string() }),
  totalCommitted: z.string(),
  /** Highest committed first. */
  rows: z.array(SpendBreakdownRowSchema),
});
export type SpendBreakdownResponse = z.infer<typeof SpendBreakdownResponseSchema>;

// ---------------------------------------------------------------------------
// GET /procurement/dashboard/monthly-spend?months=12 — purchase spend per calendar month (home dashboard widget
// "Gasto de compras por mes"). Σ purchase_orders.total by `emitted_at` month (UTC, like the dashboard periods), every
// emitted PO except anulada / rechazada_proveedor — a completed PO is still spend, unlike the dashboard's
// committedAmount, which only counts open POs. One series per currency: amounts are never converted or mixed.
// ---------------------------------------------------------------------------

export const MONTHLY_SPEND_MAX_MONTHS = 24;
export const MonthlySpendMonthsSchema = z.coerce.number().int().min(1).max(MONTHLY_SPEND_MAX_MONTHS);

export const MonthlySpendSeriesSchema = z.object({
  currency: z.string(),
  /** One amount per entry of `months`, same order; '0.00' for a month without POs. */
  amounts: z.array(z.string()),
  total: z.string(),
});
export type MonthlySpendSeries = z.infer<typeof MonthlySpendSeriesSchema>;

export const MonthlySpendResponseSchema = z.object({
  /** 'YYYY-MM', oldest first, ending with the current month. */
  months: z.array(z.string()),
  /** Highest total first; empty when no PO was emitted in the range. */
  series: z.array(MonthlySpendSeriesSchema),
});
export type MonthlySpendResponse = z.infer<typeof MonthlySpendResponseSchema>;
